import { withApiBaseUrl } from '../lib/apiBaseUrl'
import { getAccessToken } from './authService'

export interface SlipUploadRequest {
  userId: string
  username: string
  transferAmount: number
  transferTime: Date
  transferDate: Date
  slipFile: File
}

export interface SlipRecord {
  _id: string
  tenantId: string
  userId: string
  username: string
  slipId: string
  originalFileName: string
  fileExtension: string
  fileSize: number
  mimeType: string
  storagePath: string
  storageUrl: string
  bucketName: string
  transferAmount: number
  transferTime: Date
  transferDate: Date
  status: 'pending' | 'verified' | 'rejected' | 'processing'
  verifiedBy?: string
  verifiedAt?: Date
  verificationNotes?: string
  metadata: Record<string, unknown>
  isDeleted: boolean
  deletedAt?: Date
  deletedBy?: string
  createdAt: Date
  updatedAt: Date
}

export interface SlipResponse {
  success: boolean
  message: string
  data?: SlipRecord | SlipRecord[]
  pagination?: {
    currentPage: number
    totalPages: number
    totalCount: number
    hasMore: boolean
  }
  error?: string
}

class SlipStorageService {
  private baseUrl: string

  constructor() {
    this.baseUrl = withApiBaseUrl('/api/v1/wallet')
  }

  private readonly slipOkErrorMessages: Record<number, string> = {
    1000: 'กรุณาใส่ข้อมูล QR Code ให้ครบใน field data, files หรือ url',
    1001: 'ไม่พบข้อมูลสาขา กรุณาตรวจสอบไอดีสาขา',
    1002: 'Authorization Header ไม่ถูกต้อง',
    1003: 'Package ของคุณหมดอายุแล้ว',
    1004: 'Package ของคุณใช้เกินโควต้าแล้ว',
    1005: 'ไฟล์ไม่ใช่ไฟล์ภาพหรือชนิดไฟล์ไม่รองรับ',
    1006: 'รูปภาพไม่ถูกต้อง',
    1007: 'รูปภาพไม่มี QR Code',
    1008: 'QR ไม่ใช่ QR สำหรับตรวจสอบการชำระเงิน',
    1009: 'ข้อมูลธนาคารขัดข้องชั่วคราว แนะนำให้ลองใหม่อีกครั้ง',
    1010: 'สลิปธนาคารนี้ต้องรอตรวจซ้ำตามจำนวนนาทีที่ระบบแจ้ง',
    1011: 'QR Code หมดอายุ หรือ ไม่มีรายการอยู่จริง',
    1012: 'สลิปซ้ำ เคยถูกส่งเข้าระบบมาแล้ว',
    1013: 'ยอดที่ส่งมาไม่ตรงกับยอดสลิป',
    1014: 'บัญชีผู้รับไม่ตรงกับบัญชีหลักของร้าน',
  }

  private extractSlipUploadMessage(body: any, status: number): string {
    const nestedSlipMessage = String(
      body?.data?.response?.error?.message
      || body?.response?.error?.message
      || body?.data?.error?.message
      || ''
    ).trim()

    if (nestedSlipMessage) {
      return nestedSlipMessage
    }

    const slipErrorCode = Number(
      body?.data?.response?.error?.code
      ?? body?.response?.error?.code
      ?? body?.data?.error?.code
    )
    if (Number.isFinite(slipErrorCode) && this.slipOkErrorMessages[slipErrorCode]) {
      return this.slipOkErrorMessages[slipErrorCode]
    }

    return body?.message || `อัปโหลดสลิปไม่สำเร็จ (HTTP ${status})`
  }

  async uploadSlip(tenantId: string, slipData: SlipUploadRequest): Promise<SlipResponse> {
    const SLIP_UPLOAD_CLIENT_TIMEOUT_MS = 60000
    const SLIP_UPLOAD_RETRY_DELAYS_MS = [1500, 4000]
    const isRetryableStatus = (status: number): boolean =>
      status === 502 || status === 503 || status === 504

    const buildFormData = (): FormData => {
      const formData = new FormData()
      formData.append('slipFile', slipData.slipFile)
      formData.append('userId', slipData.userId)
      formData.append('username', slipData.username)
      formData.append('transferAmount', String(slipData.transferAmount))
      formData.append('transferTime', slipData.transferTime.toISOString())
      formData.append('transferDate', slipData.transferDate.toISOString())
      return formData
    }

    const performUpload = async (): Promise<{ ok: boolean; status: number; body: any }> => {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), SLIP_UPLOAD_CLIENT_TIMEOUT_MS)
      try {
        const response = await fetch(`${this.baseUrl}/${tenantId}/slips/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getAccessToken() || ''}`,
            'X-Tenant-ID': tenantId,
            'X-User-ID': slipData.userId,
          },
          body: buildFormData(),
          credentials: 'include',
          signal: controller.signal,
        })
        const body = (await response.json().catch(() => ({}))) as any
        return { ok: response.ok, status: response.status, body }
      } finally {
        clearTimeout(timer)
      }
    }

    const maxAttempts = SLIP_UPLOAD_RETRY_DELAYS_MS.length + 1
    let lastError: any = null

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const { ok, status, body } = await performUpload()
        if (ok) return body as SlipResponse

        if (!isRetryableStatus(status)) {
          const message = this.extractSlipUploadMessage(body, status)
          return { success: false, message, error: body?.error || body?.data?.response?.error?.type || message }
        }

        lastError = new Error(this.extractSlipUploadMessage(body, status))
      } catch (error: any) {
        lastError = error
        if (error?.name === 'AbortError') {
          lastError = new Error('การอัปโหลดสลิปใช้เวลานานเกินไป กรุณาลองใหม่')
        }
      }

      const delay = SLIP_UPLOAD_RETRY_DELAYS_MS[attempt]
      if (delay && attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay))
      }
    }

    console.error('Slip upload failed after retries:', lastError)
    const message = lastError?.message || 'เกิดข้อผิดพลาดในการอัปโหลดสลิป กรุณาลองใหม่อีกครั้ง'
    return { success: false, message, error: message }
  }

  async getUserSlips(
    tenantId: string,
    userId: string,
    options: {
      page?: number
      limit?: number
      status?: 'all' | 'pending' | 'verified' | 'rejected' | 'processing'
    } = {}
  ): Promise<SlipResponse> {
    try {
      const { page = 1, limit = 20, status = 'all' } = options
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        status,
      })

      const response = await fetch(`${this.baseUrl}/${tenantId}/users/${userId}/slips?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken() || ''}`,
          'X-Tenant-ID': tenantId,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })

      const result = (await response.json().catch(() => ({}))) as SlipResponse
      if (!response.ok) {
        throw new Error(result.message || 'Failed to get user slips')
      }

      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการดึงข้อมูลสลิป'
      console.error('Get user slips failed:', error)
      return {
        success: false,
        message,
        error: message,
      }
    }
  }
}

export const slipStorageService = new SlipStorageService()
